import { CustomNodeType, generateNodeConfig } from './nodes';
import { ActionBtns } from './nodes/common';
import { TreeGraphData } from '@antv/g6';
import {
  fullPkgOptions,
  fullMoneyOptions,
  operationTypeOptions,
  startPkgOptions,
  fullTypeOptions,
  activityTypeOptions,
  fullGwpRightsOperationOptions,
  fullSubRightsOperationOptions,
  fullGwpTimesOptions,
  fullSubTimesOptions
} from './options';
import { generateRandomNumber } from './utils';

export const createConditionRowJsonData = (isPre = false) => {
  const conditionId = generateRandomNumber();
  return {
    id: conditionId,
    _uniqueId: generateRandomNumber(),
    specialCharacters: conditionId,
    optionType: operationTypeOptions[0].value, // 操作类型：满金额，满数量，满款数等
    optionValue: 1, // 数值
    orderUnit: startPkgOptions[0].value, // 起订单位：xx起订
    fullNumber: 1, // 需满数值
    moneyUnit: fullMoneyOptions[0].value, // 需满单位-金额，操作类型为满金额时填
    itemUnit: fullTypeOptions[0].value, // 需满单位-商品，操作类型为满款数时填
    packageUnit: fullPkgOptions[0].value, // 需满单位-包装，操作类型为满数量时填
    promotionItemStr: '', // 商品清单名称
    checked: false, // 是否勾选(仅前端使用)
    checkedDisabled: false // 勾选是否禁用(仅前端使用)
  };
};

export const createJsonData = (id, parentId, level) => {
  return {
    id,
    parentId,
    name: '名称',
    hasPrecondition: false, // 是否开启前置条件
    level, // 节点层级
    // 前置条件
    conditions: [],
    conditionDesc: '', // 活动提示文案
    logicalExpression: '', // 前置条件表达式
    // 促销政策
    policyInfo: {
      name: '名称',
      // 主题名称
      policyThemes: '',
      // 政策标签
      policyLabel: '',
      // 下单金额限制
      orderNumberLimit: 0,
      // 记账时间规则
      accountTimeRule: '',
      // 账期天数
      paymentDays: '',
      // 记账时间
      accountDate: '',
      // 促销类型
      promotionType: ''
    },
    promotionItemStr: '', // 促销政策商品清单
    priority: 1, // 活动类型优先级
    optionType: '', // 操作类型
    effectConditions: [], // 满减/满赠门槛规则
    effectConditionDesc: '', // 活动提示文案
    effectLogicalExpression: '', // 满减/满赠门槛规则表达式
    directDescentOption: '', // 直降门槛规则
    // 权益信息
    equityInformationRule: {
      name: '',
      reductionOption: fullSubRightsOperationOptions[0].value, //活动类型=满减时的优惠操作
      giftOption: fullGwpRightsOperationOptions[0].value, //活动类型=满赠时的优惠操作
      reductionDiscount: fullSubTimesOptions[0].value, //操作类型为满减时的优惠类型
      giftDiscount: fullGwpTimesOptions[0].value, //操作为满赠时的优惠类型
      discountValue: 0, //优惠数值
      giftItemStr: '', // 满赠清单
      equityInfoItemStr: '', // 权益信息清单
      realPriceNotBenefits: false, // 实价产品不让利
      buyItNotBenefits: false, // 一口价产品不让利
      isAdvanced: false, // 是否高级配置
      advanceItemStr: '' // 高级配置清单
    },
    children: [],
    // 仅前端使用, 前置条件组合
    conditionsCombine: {
      type: 'and',
      groups: []
    },
    // 仅前端使用, 门槛规则组合
    effectConditionsCombine: {
      type: 'and',
      groups: []
    }
  };
};

export const rootNodeData = (jsonData): TreeGraphData => {
  return {
    id: jsonData.id,
    style: {
      titleBgColor: 'var(--oio-button-primary-background)'
    },
    actionBar: { switchAction: { open: false }, addAction: true, deleteAction: true } as Partial<ActionBtns>,
    ...generateNodeConfig(CustomNodeType.PLAN),
    children: [] as TreeGraphData[],
    jsonData
  };
};

export const createConditionData = (isPre = false, jsonData, isEdit = false) => {
  const conditionRow = createConditionRowJsonData(true);
  const effectConditionRow = createConditionRowJsonData();

  const c = {
    id: generateRandomNumber(),
    title: isPre ? '前置条件' : '门槛规则',
    isPre,
    style: {
      titleBgColor: isPre ? '#d70a25' : 'var(--oio-button-primary-background)'
    },
    actionBar: { settingAction: false } as Partial<ActionBtns>,
    ...generateNodeConfig(CustomNodeType.CONDITIONS),
    children: [] as TreeGraphData[],
    jsonData
  };

  if (!isEdit) {
    if (isPre) {
      c.jsonData.conditions = [conditionRow];
      c.jsonData.conditionsCombine.groups = [
        {
          _uniqueIds: [conditionRow._uniqueId],
          type: 'and'
        }
      ];
    } else {
      c.jsonData.effectConditions = [effectConditionRow];
      c.jsonData.effectConditionsCombine.groups = [
        {
          _uniqueIds: [effectConditionRow._uniqueId],
          type: 'and'
        }
      ];
    }
  }

  return c;
};

export const createSimpleConditionData = (jsonData) => {
  return {
    id: generateRandomNumber(),
    title: '门槛规则',
    style: {
      titleBgColor: 'var(--oio-button-primary-background)'
    },
    actionBar: { settingAction: false } as Partial<ActionBtns>,
    ...generateNodeConfig(CustomNodeType.SIMPLE_CONDITION),
    children: [] as TreeGraphData[],
    jsonData
  };
};

export const createPolicyData = (parentId, jsonData) => {
  return {
    id: jsonData.id,
    style: {
      titleBgColor: '#aaa'
    },
    actionBar: {
      switchAction: { open: false },
      linkAction: true,
      addAction: true,
      settingAction: true,
      deleteAction: true
    } as Partial<ActionBtns>,
    ...generateNodeConfig(CustomNodeType.POLICY),
    children: [] as TreeGraphData[],
    jsonData
  };
};

export const createActivityTypeData = (parentId, jsonData) => {
  return {
    id: jsonData.id,
    title: '活动类型',
    style: {
      titleBgColor: 'var(--oio-button-primary-background)'
    },
    actionBar: { switchAction: { open: false }, deleteAction: true } as Partial<ActionBtns>,
    ...generateNodeConfig(CustomNodeType.ACTIVITY_TYPE),
    children: [] as TreeGraphData[],
    jsonData
  };
};

export const createRightsInfoData = (jsonData) => {
  return {
    id: generateRandomNumber(),
    title: '',
    activityType: activityTypeOptions[0].value, // 活动类型
    col1DisabledOptions: [], // 第一列禁用选项
    policyId: '', // 所属促销政策节点id
    style: {
      titleBgColor: '#f39a35'
    },
    actionBar: {
      linkAction: true,
      settingAction: true
    } as Partial<ActionBtns>,
    ...generateNodeConfig(CustomNodeType.RIGHTS_INFO),
    children: [] as TreeGraphData[],
    jsonData
  };
};
